/**
 * « Ma dispo » : ce que `<TutorAvailabilityPage>` montre, lu dans le store (patron A : la semaine
 * servie est rangée par le réducteur `tutorAvailability`, `weekView`) et dans le brouillon de l'écran.
 *
 * Port de `MaDispoScreen.js` : le brouillon (`draftOpenSlots`) n'écrit rien, il remplace les créneaux
 * proposés de la semaine servie ; `null` = « on suit le serveur ». Un brouillon vidé reste un
 * brouillon (il retire le tuteur des propositions) : il s'enregistre. Le bandeau lit la semaine
 * ENREGISTRÉE — un brouillon dé-confirme la semaine sur-le-champ.
 *
 * Un `createSelector` sur les tranches qu'il lit et sur le brouillon (`ports/CLAUDE.md`).
 */
import { createSelector } from "reselect"

import {
    type ApiTutorWeekView,
    canonicalSlots,
    openSlotsOf,
    startingDateOpensOn,
    toUITutorWeek,
    tutorWeekFreshness,
} from "../../../adapters/tutor/shared/tutorWeekAdapter"
import { type UITutorWeek, type UITutorWeekCta, type UITutorWeekFreshness } from "../../../ui/types/tutorDispoOffres"

export type TutorAvailabilityViewModel = {
    /** `null` tant que la semaine n'est pas là : rien de vrai à montrer avant. */
    week: UITutorWeek | null
    freshness: UITutorWeekFreshness
    cta: UITutorWeekCta
    /** Une semaine déclarée, ou un brouillon (même vidé) : il y a quelque chose à enregistrer. */
    canSave: boolean
    /** Le brouillon diffère de la semaine servie (ordre et padding ignorés). */
    dirty: boolean
    /** Les créneaux proposés tels que l'enregistrement les enverra. */
    openSlots: string[]
    /** Les créneaux proposés de la semaine servie : la base d'une modification du brouillon. */
    serverOpenSlots: string[]
    startingDate: string | null
    startingDateOpensOn: string
    loading: boolean
    /** L'échec du chargement, seulement sans semaine en main : une relecture ratée n'efface rien. */
    error: unknown
}

type AvailabilityState = {
    data?: { tutorAvailability?: { weekView?: ApiTutorWeekView | null } | null }
    api?: { getAvailability?: { error?: unknown } | null }
}

const root = (state: unknown) => state as AvailabilityState

const selectAvailability = createSelector(
    [
        (state: unknown) => root(state)?.data?.tutorAvailability?.weekView ?? null,
        (state: unknown) => root(state)?.api?.getAvailability?.error ?? null,
        (_state: unknown, draft: string[] | null) => draft,
    ],
    (week, loadError, draft): TutorAvailabilityViewModel => {
        const serverOpenSlots = openSlotsOf(week)
        const openSlots = draft ?? serverOpenSlots
        const dirty = draft != null && canonicalSlots(draft) !== canonicalSlots(serverOpenSlots)
        const freshness = tutorWeekFreshness(week, dirty)
        const canSave = !!week?.has_declared_slots || dirty
        const startingDate = week?.availability_starting_date ?? null
        return {
            week: week == null ? null : toUITutorWeek(week, openSlots),
            freshness,
            cta: freshness === "verified" ? "dashboard" : freshness === "needsSlots" && !dirty ? "add" : "confirm",
            canSave,
            dirty,
            openSlots,
            serverOpenSlots,
            startingDate,
            startingDateOpensOn: startingDateOpensOn(startingDate),
            loading: week == null && loadError == null,
            error: week == null ? loadError : null,
        }
    }
)

export function tutorAvailabilityViewModel(state: unknown, draft: string[] | null): TutorAvailabilityViewModel {
    return selectAvailability(state, draft)
}
